import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";
import { useQueryClient } from "@tanstack/react-query";
import { ValidationButton } from "./validation/ValidationButton";
import { ExplanationForm } from "./explanation/ExplanationForm";

interface CountedAmountInputProps {
  method: string;
  counted?: number;
  amount: number;
  onChange: (value: string) => void;
  isDisabled?: boolean;
}

export const CountedAmountInput = ({
  method,
  counted,
  amount,
  onChange,
  isDisabled,
}: CountedAmountInputProps) => {
  const [explanation, setExplanation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastValidatedAmount, setLastValidatedAmount] = useState<number | null>(null);
  const { data: currencySymbol } = useOwnerCurrency();
  const queryClient = useQueryClient();

  useEffect(() => {
    setExplanation("");
    checkLastValidation();
  }, [counted, method]);

  const checkLastValidation = async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data, error } = await supabase
      .from('validated_amounts')
      .select('amount')
      .eq('payment_method', method)
      .eq('counting_date', today)
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error("Erreur lors de la vérification des validations:", error);
      return;
    }

    if (data && data.length > 0) {
      setLastValidatedAmount(data[0].amount);
    } else {
      setLastValidatedAmount(null);
    }
  };

  const handleValidateAmount = async () => {
    if (!counted) return;
    
    if (lastValidatedAmount !== null && lastValidatedAmount === counted) {
      toast.error("Ce montant a déjà été validé");
      return;
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Utilisateur non authentifié");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('validated_amounts')
        .insert({
          counting_date: new Date().toISOString().split('T')[0],
          payment_method: method,
          amount: counted,
          status: 'validated',
          owner_id: user.id
        });

      if (error) throw error;
      toast.success("Nouvelles transactions validées avec succès");
      setLastValidatedAmount(counted);
      
      await queryClient.invalidateQueries({ queryKey: ["paymentSummaries"] });
      await queryClient.invalidateQueries({ queryKey: ["validatedAmounts"] });
    } catch (error) {
      console.error("Erreur lors de la validation du montant:", error);
      toast.error("Erreur lors de la validation du montant");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExplanationSubmit = async () => {
    if (!counted) return;
    
    const difference = counted - amount;
    if (difference === 0) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Utilisateur non authentifié");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('counting_discrepancies')
        .insert({
          payment_method: method,
          expected_amount: amount,
          counted_amount: counted,
          difference_amount: difference,
          explanation,
          counting_date: new Date().toISOString().split('T')[0],
          created_by: user.id
        });

      if (error) throw error;
      toast.success("Différence enregistrée avec succès");
      setExplanation("");
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la différence:", error);
      toast.error("Erreur lors de l'enregistrement de la différence");
    } finally {
      setIsSubmitting(false);
    }
  };

  const difference = counted !== undefined ? counted - amount : 0;
  const newTransactionsAmount = lastValidatedAmount !== null && counted !== undefined
    ? counted - lastValidatedAmount
    : counted;

  // Désactiver l'input si le montant a déjà été validé et correspond au montant attendu
  const isInputDisabled = isDisabled || (lastValidatedAmount !== null && lastValidatedAmount === amount);

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`counted-${method}`}>Montant compté</Label>
        <Input
          id={`counted-${method}`}
          type="number"
          value={counted || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Saisissez le montant compté"
          disabled={isInputDisabled}
          className={`${isInputDisabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        />
        {counted !== undefined && (
          <>
            <div
              className={`text-sm mt-1 ${
                difference === 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              Différence: {difference.toLocaleString()} {currencySymbol}
            </div>
            {lastValidatedAmount !== null && (
              <div className="text-sm mt-1 text-blue-600">
                Nouvelles transactions: {newTransactionsAmount?.toLocaleString()} {currencySymbol}
              </div>
            )}
          </>
        )}
      </div>

      {counted !== undefined && !isInputDisabled && (
        <ValidationButton
          isSubmitting={isSubmitting}
          isDisabled={lastValidatedAmount === counted}
          lastValidatedAmount={lastValidatedAmount}
          onValidate={handleValidateAmount}
        />
      )}

      {counted !== undefined && difference !== 0 && !isInputDisabled && (
        <ExplanationForm
          explanation={explanation}
          onExplanationChange={setExplanation}
          onSubmit={handleExplanationSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
};